/**
 * src/pages/Login.jsx — sign in, and first-run studio registration.
 *
 * Registration lives on the same page rather than its own route because this
 * is a self-serve product: the person arriving here either has an account or
 * is about to create one, and a separate /register route is one more thing to
 * find.
 */
import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../lib/auth.jsx';
import { ErrorBanner } from '../components/ui.jsx';

/** Where the legal pages live: the marketing site, not this app. */
const LEGAL_BASE = 'https://www.tattoo-lab.co.uk';

export default function Login() {
  const { artist, loading, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({
    email: '',
    password: '',
    studioName: '',
    slug: '',
    name: '',
    acceptTerms: false,
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Already signed in: go straight where they were headed.
  if (!loading && artist) {
    return <Navigate to={location.state?.from ?? '/dashboard'} replace />;
  }

  /**
   * Updates one form field.
   *
   * @param {string} key
   * @returns {(e: React.ChangeEvent<HTMLInputElement>) => void}
   */
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  /**
   * Suggests a URL slug from the studio name as the owner types it, so they
   * rarely have to think about the field at all.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const onStudioName = (e) => {
    const studioName = e.target.value;
    setForm((f) => ({
      ...f,
      studioName,
      slug: slugify(studioName),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
      } else {
        await register({
          studioName: form.studioName,
          slug: form.slug,
          email: form.email,
          password: form.password,
          name: form.name || form.studioName,
          acceptTerms: form.acceptTerms,
        });
      }
      navigate(location.state?.from ?? '/dashboard', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="public-page" style={{ maxWidth: 420 }}>
      <div className="public-header">
        <img src="/favicon.svg" alt="" width="40" height="40" />
        <h1>{mode === 'login' ? 'Log in to Tattoo Lab' : 'Set up your studio'}</h1>
        <p className="muted small">
          {mode === 'login'
            ? 'Sign in to your studio.'
            : 'Set up your studio in one step — £49.99/month, cancel anytime.'}
        </p>
      </div>

      <form className="card stack" onSubmit={submit}>
        {/* Set by ResetPassword.jsx after a successful reset. */}
        {location.state?.notice && !error ? (
          <div className="alert alert-success" role="status">
            {location.state.notice}
          </div>
        ) : null}
        <ErrorBanner>{error}</ErrorBanner>

        {mode === 'register' ? (
          <>
            <div>
              <label htmlFor="studioName">Studio name</label>
              <input
                id="studioName"
                value={form.studioName}
                onChange={onStudioName}
                required
                autoComplete="organization"
              />
            </div>
            <div>
              <label htmlFor="slug">Booking URL</label>
              <input id="slug" value={form.slug} onChange={set('slug')} required />
              <p className="muted small" style={{ marginTop: 4 }}>
                Clients will book at /book/{form.slug || 'your-studio'}
              </p>
            </div>
            <div>
              <label htmlFor="name">Your name</label>
              <input id="name" value={form.name} onChange={set('name')} autoComplete="name" />
            </div>
          </>
        ) : null}

        <div>
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            placeholder="alan.turing@example.com"
            value={form.email}
            onChange={set('email')}
            required
            autoComplete="email"
          />
        </div>
        <div>
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            value={form.password}
            onChange={set('password')}
            required
            minLength={8}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          />
          {/* Login mode only: someone setting up a studio is choosing a
              password, not trying to remember one. */}
          {mode === 'login' ? (
            <p className="small" style={{ marginTop: 4 }}>
              <Link to="/forgot-password">Forgot your password?</Link>
            </p>
          ) : null}
        </div>

        {/* Register mode only. Required here and by the API: creating a
            studio is what binds it to these, and the API records which
            version was accepted (api/src/lib/legal.js). The documents live
            on the marketing site, so they open in a new tab rather than
            losing the half-filled form. */}
        {mode === 'register' ? (
          <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={form.acceptTerms}
              onChange={(e) => setForm((f) => ({ ...f, acceptTerms: e.target.checked }))}
              required
              style={{ width: 'auto', marginTop: 3 }}
            />
            <span>
              I agree to the{' '}
              <a href={`${LEGAL_BASE}/terms`} target="_blank" rel="noopener">Terms of Service</a>,{' '}
              <a href={`${LEGAL_BASE}/dpa`} target="_blank" rel="noopener">Data Processing Agreement</a> and{' '}
              <a href={`${LEGAL_BASE}/privacy`} target="_blank" rel="noopener">Privacy Policy</a>
            </span>
          </label>
        ) : null}

        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Working…' : mode === 'login' ? 'Sign in' : 'Create studio'}
        </button>

        <button
          type="button"
          className="btn"
          onClick={() => {
            setMode(mode === 'login' ? 'register' : 'login');
            setError(null);
          }}
        >
          {mode === 'login' ? 'Set up a new studio' : 'I already have an account'}
        </button>
      </form>
    </div>
  );
}

/**
 * Turns a studio name into a URL-safe slug matching the API's validation.
 *
 * @param {string} value
 * @returns {string} e.g. "Ink by Maya" → "ink-by-maya".
 */
function slugify(value) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
